import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Security,
  Code,
  Group,
  Verified,
  Lock,
} from '@mui/icons-material';

const AboutPage = () => {
  const theme = useTheme();

  const values = [
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: 'Security Excellence',
      description: 'We approach security as both an art and a science, creating solutions that protect without compromising usability.',
    },
    {
      icon: <Group sx={{ fontSize: 48 }} />,
      title: 'Partnership',
      description: 'We collaborate closely with our clients to understand their unique challenges and build lasting relationships.',
    },
    {
      icon: <Code sx={{ fontSize: 48 }} />,
      title: 'Innovation',
      description: 'We push boundaries by combining cutting-edge technology with creative problem-solving approaches.',
    },
    {
      icon: <Verified sx={{ fontSize: 48 }} />,
      title: 'Transparency',
      description: 'Open source foundations and clear communication build the trust that modern organizations require.',
    },
  ];


  return (
    <Box sx={{ minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          {/* Kief Studio Logo */}
          <Box
            component="img"
            src={theme.palette.mode === 'dark' 
              ? "/img/KS-Logo-Full-Light.png" 
              : "/img/KS-Logo-Full-Dark.png"
            }
            alt="Kief Studio"
            sx={{
              height: { xs: 60, sm: 80 },
              width: 'auto',
              mb: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
          <Typography
            variant="h2"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent?.main || theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            About Kief Studio
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
            We help security professionals and organizations build trust through innovative password management solutions.
          </Typography>
        </Box>

        {/* Mission Statement */}
        <Box
          sx={{
            textAlign: 'center',
            p: 6,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.accent?.main || theme.palette.secondary.main, 0.1)} 100%)`,
            border: '1px solid',
            borderColor: 'divider',
            mb: 8,
          }}
        >
          <Lock sx={{ fontSize: 64, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 3 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Our Mission
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
            At Kief Studio, we believe that security tools should be both powerful and accessible. PWGen-Rust represents our commitment to 
            delivering enterprise-grade password management without compromising on user privacy or performance. Built with Rust's 
            memory-safe foundation, PWGen-Rust embodies our philosophy of merging technical excellence with practical security solutions 
            that organizations can trust and rely on.
          </Typography>
        </Box>

        {/* Core Values */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            What Drives Us
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" paragraph sx={{ mb: 6 }}>
            Our core values shape how we approach every project and partnership at Kief Studio.
          </Typography>
          
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ color: theme.palette.accent?.main || theme.palette.primary.main, mb: 3 }}>
                      {value.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>


        {/* Company Information */}
        <Box
          sx={{
            textAlign: 'center',
            p: 6,
            borderRadius: 2,
            bgcolor: theme.palette.mode === 'dark' ? alpha('#000', 0.2) : alpha('#000', 0.02),
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Code sx={{ fontSize: 64, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 3 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Ready to Partner with Us?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
            Whether you're looking to enhance your organization's security posture or explore custom solutions, 
            Kief Studio is here to help you navigate the intersection of security and innovation.
          </Typography>
          
          <Stack direction="row" spacing={4} justifyContent="center" flexWrap="wrap" useFlexGap>
            {[
              { label: 'Security Consulting', href: 'https://kief.studio' },
              { label: 'Custom Development', href: 'https://kief.studio' },
              { label: 'Enterprise Support', href: '/contact' },
              { label: 'Training & Workshops', href: 'https://kief.studio' }
            ].map((service) => (
              <Box
                key={service.label}
                component="a"
                href={service.href}
                target={service.href.startsWith('http') ? '_blank' : undefined}
                sx={{
                  px: 3,
                  py: 1,
                  border: '1px solid',
                  borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                  borderRadius: 20,
                  color: theme.palette.accent?.main || theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                    color: 'white',
                  },
                }}
              >
                {service.label}
              </Box>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutPage;